import { useState } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Press, haptic } from '@dorixona/ui'
import { Icon, type IconName } from '../ui/Icon'
import { QrSheet } from '../features/account/QrSheet'
import { useCart } from '../features/cart/CartProvider'
import { useActiveOrderCount, useLoyalty } from '../features/account/api'
import { profileView } from '../features/account/model'
import { useSession } from './session'
import { catalogTabTarget, useCatalogMemory } from './catalogMemory'

interface Tab {
  to: string
  key: 'home' | 'catalog' | 'cart' | 'orders'
  icon: IconName
  /** Залитый вариант — на активной вкладке, как в системной панели iOS. */
  iconOn: IconName
}

/**
 * Разделы приложения. Действие — только QR, и оно выглядит иначе. Аптеки сети
 * открываются из личного кабинета.
 */
const LEFT_TABS: Tab[] = [
  { to: '/', key: 'home', icon: 'i-home', iconOn: 'i-home-fill' },
  { to: '/catalog', key: 'catalog', icon: 'i-pill', iconOn: 'i-pill-fill' },
]

const RIGHT_TABS: Tab[] = [
  { to: '/cart', key: 'cart', icon: 'i-cart', iconOn: 'i-cart-fill' },
  { to: '/orders', key: 'orders', icon: 'i-box', iconOn: 'i-box-fill' },
]

/**
 * Нижняя панель. Вкладки — разделы, между ними одно действие: показать карту
 * на кассе. HIG (tab-bars.md › Use a tab bar to support navigation, not to
 * provide actions) советует держать в панели только разделы, поэтому QR
 * намеренно не выглядит вкладкой: он приподнят и залит акцентом, чтобы
 * читаться как кнопка. Гостю он тоже виден: шторка скажет, что кэшбэк
 * копится после входа, и предложит войти.
 */
export function TabBar() {
  const { t } = useTranslation('common')
  const { count } = useCart()
  const { isAuthenticated, customer } = useSession()
  const loyalty = useLoyalty(isAuthenticated)
  const activeOrders = useActiveOrderCount(isAuthenticated)
  const [qrOpen, setQrOpen] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  useCatalogMemory()

  // Счётчик на вкладке: в корзине — сколько товаров, в заказах — сколько ещё в работе.
  const badges: Partial<Record<Tab['key'], number>> = { cart: count, orders: activeOrders }

  const tab = (item: Tab) => {
    const badge = badges[item.key] ?? 0
    return (
      <NavLink
        key={item.key}
        // Каталог открывается с оставленным отбором, остальные разделы — с начала.
        to={item.key === 'catalog' ? catalogTabTarget(location) : item.to}
        end={item.to === '/'}
        // Переключение раздела — то немногое, где отдача уместна: палец получает
        // подтверждение раньше, чем глаз заметит смену экрана.
        onClick={() => {
          haptic('select')
        }}
      >
        {({ isActive }) => (
          <>
            <span className="tabbar__icon">
              <Icon name={isActive ? item.iconOn : item.icon} />
              {badge > 0 ? <span className="tab-badge num">{badge}</span> : null}
            </span>
            {t(`tabs.${item.key}`)}
          </>
        )}
      </NavLink>
    )
  }

  return (
    <>
      <div className="tabbar-fade" aria-hidden="true" />
      <nav className="tabbar" aria-label={t('tabs.home')}>
        {LEFT_TABS.map(tab)}

        <Press
          squish="card"
          haptic="select"
          className="tabbar__qr"
          aria-label={t('actions.showQrFull')}
          onPress={() => {
            setQrOpen(true)
          }}
        >
          <span className="tabbar__qr-key">
            <Icon name="i-qr" size={26} />
          </span>
        </Press>

        {RIGHT_TABS.map(tab)}
      </nav>

      <QrSheet
        opened={qrOpen}
        code={loyalty.data?.qr ?? ''}
        phone={loyalty.data?.phone || profileView(customer).phone}
        scanMode={loyalty.data?.scanMode ?? 'phone_accrual'}
        onClose={() => {
          setQrOpen(false)
        }}
        guest={!isAuthenticated}
        onSignIn={() => {
          setQrOpen(false)
          void navigate('/login')
        }}
      />
    </>
  )
}
